// components/secure/pm/PMRulesAndFees.js
// Property Management → Rules, fees & defaults (managing broker; admin reads).
//
// Three things that used to change only through scripts/seed-property-management.js
// or SQL, although they change on the calendar:
//   * the rule layers — above all Chicago's security-deposit interest rate, which the
//     City Comptroller sets every January;
//   * the office fee template every new management agreement copies;
//   * the default reserve floor and the base-fee scope printed on agreements.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../../hooks/useApiClient';
import { Panel, Table, Modal, Input, Select, Textarea, Button, Notice, Pill, Tabs, titleCase } from './PMKit';

const LAYER_FIELDS = [
    ['Security deposit', [
        ['deposit_cap_months', 'Deposit cap (months of rent)'],
        ['deposit_interest_required', 'Interest required', 'bool'],
        ['deposit_interest_rate', 'Interest rate (%) — e.g. 0.01 means 0.01%'],
        ['deposit_interest_period_months', 'Interest paid every (months)'],
        ['deposit_interest_due_days', 'Interest due within (days of period end)'],
        ['deposit_itemize_days', 'Itemised deductions within (days)'],
        ['deposit_return_days', 'Deposit returned within (days)'],
        ['deposit_bank_disclosure', 'Bank disclosure', ['none', 'name', 'name_and_address']],
        ['deposit_waiver_allowed', 'Waiver allowed', 'bool'],
    ]],
    ['Late fee', [
        ['late_fee_model', 'Model', ['none', 'percent', 'flat', 'chicago_formula']],
        ['late_fee_percent', 'Percent'],
        ['late_fee_flat', 'Flat ($)'],
        ['late_fee_base_amount', 'Formula: first ($ of rent)'],
        ['late_fee_base_charge', 'Formula: charge on that ($)'],
        ['late_fee_excess_percent', 'Formula: % of the excess'],
    ]],
    ['Notices', [
        ['notice_tier_1_days', 'Increase / non-renewal notice, tier 1 (days)'],
        ['notice_tier_1_max_months', 'Tier 1 applies up to (months of tenancy)'],
        ['notice_tier_2_days', 'Tier 2 (days)'],
        ['notice_tier_2_max_months', 'Tier 2 applies up to (months)'],
        ['notice_tier_3_days', 'Tier 3 (days)'],
        ['notice_nonpayment_days', 'Non-payment notice (days)'],
        ['notice_violation_days', 'Lease violation notice (days)'],
        ['notice_month_to_month_days', 'Month-to-month termination (days)'],
    ]],
];
const BASES = ['percent_collected', 'percent_gross', 'flat_monthly', 'flat_per_unit', 'percent_one_month', 'flat_one_time', 'percent_markup'];

export default function PMRulesAndFees() {
    const api = useApiClient();
    const [allowed, setAllowed] = useState(null); // null = loading, false = hidden
    const [canEdit, setCanEdit] = useState(false);
    const [tab, setTab] = useState('layers');
    const [layers, setLayers] = useState([]);
    const [fees, setFees] = useState([]);
    const [settings, setSettings] = useState(null);
    const [scopeText, setScopeText] = useState({ included: '', excluded: '' });
    const [layer, setLayer] = useState(null);
    const [fee, setFee] = useState(null);
    const [msg, setMsg] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const s = await api.get('/api/property-management/settings');
            const [j, t, g] = await Promise.all([
                api.get('/api/property-management/jurisdictions'),
                api.get('/api/property-management/fee-template'),
                api.get('/api/property-management/grants/mine').catch(() => null),
            ]);
            setSettings(s);
            setScopeText({ included: (s.base_fee_scope.included || []).join('\n'), excluded: (s.base_fee_scope.excluded || []).join('\n') });
            setLayers(j || []); setFees(t?.items || []);
            setCanEdit(!!g?.managing_broker);
            setAllowed(true);
        } catch (e) { setAllowed(e.status === 403 ? false : true); if (e.status !== 403) setError(e.message); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const act = async (fn) => {
        setBusy(true); setError(''); setMsg('');
        try { const r = await fn(); if (r?.message) setMsg(r.message); await load(); return true; }
        catch (e) { setError(e.message); return false; } finally { setBusy(false); }
    };

    if (allowed === false || allowed === null) return null;

    const saveLayer = async () => {
        const body = {};
        for (const [, fields] of LAYER_FIELDS) for (const [k, , kind] of fields) {
            const v = layer[k];
            if (kind === 'bool') body[k] = Number(v) ? 1 : 0;
            else if (Array.isArray(kind)) body[k] = v || kind[0]; // NOT NULL enums: never send null
            else body[k] = v === '' || v === undefined ? null : v;
        }
        body.notes = layer.notes || null;
        if (await act(() => api.put(`/api/property-management/jurisdictions/${layer.jurisdiction_id}`, body))) setLayer(null);
    };
    const saveFee = async () => {
        const body = {};
        for (const k of ['label', 'basis', 'rate', 'amount', 'minimum_amount', 'maximum_amount', 'notes']) body[k] = fee[k] === '' ? null : fee[k];
        body.is_active = fee.is_active ? 1 : 0;
        if (await act(() => api.put(`/api/property-management/fee-items/${fee.fee_item_id}`, body))) setFee(null);
    };
    const saveDefaults = () => act(() => api.put('/api/property-management/settings', {
        reserve_floor_per_unit: settings.reserve_floor_per_unit,
        base_fee_scope: { included: scopeText.included.split('\n'), excluded: scopeText.excluded.split('\n') },
    }));

    const chicago = layers.find((l) => /chicago/i.test(l.name) && l.deposit_interest_required);

    return (
        <Panel title="Rules, fees & defaults"
            subtitle={canEdit ? 'The managing broker keeps these current. Changes apply from now on; executed agreements and posted entries are not rewritten.' : 'Read-only: the managing broker edits these.'}>
            {error && <Notice tone="bad" message={error} onDismiss={() => setError('')} />}
            {msg && <Notice tone="good" message={msg} onDismiss={() => setMsg('')} />}
            {chicago && new Date().getMonth() === 0 && (
                <Notice tone="warn" title="January: check Chicago's deposit interest rate"
                    message={`The City Comptroller publishes the new security-deposit interest rate each year. On file: ${chicago.deposit_interest_rate ?? 'none'}%.`} />
            )}
            <Tabs active={tab} onChange={setTab} tabs={[
                { key: 'layers', label: 'Rule layers', count: layers.length },
                { key: 'fees', label: 'Fee template', count: fees.length },
                { key: 'defaults', label: 'Defaults' },
            ]} />

            {tab === 'layers' && (
                <Table
                    columns={[
                        { key: 'layer', label: 'Layer', render: (r) => titleCase(r.layer) },
                        { key: 'name', label: 'Name' },
                        { key: 'deposit_interest_rate', label: 'Deposit interest', render: (r) => (r.deposit_interest_required ? `${r.deposit_interest_rate ?? '—'}%` : '—') },
                        { key: 'late_fee_model', label: 'Late fee', render: (r) => titleCase(r.late_fee_model) },
                        { key: 'deposit_return_days', label: 'Return deposit (days)', align: 'right' },
                        { key: 'updated_at', label: 'Last changed', nowrap: true, render: (r) => (r.updated_at ? String(r.updated_at).slice(0, 10) : '—') },
                    ]}
                    rows={layers.map((l) => ({ ...l, _key: l.jurisdiction_id }))}
                    onRowClick={canEdit ? (r) => setLayer({ ...r }) : undefined}
                    empty="No rule layers are loaded."
                />
            )}

            {tab === 'fees' && (
                <>
                    <p className="text-xs text-slate-500 mb-2">Every new management agreement copies these. A draft agreement's own fees are edited on the agreement; an executed one is never changed.</p>
                    <Table
                        columns={[
                            { key: 'label', label: 'Fee' },
                            { key: 'applies_to', label: 'Applies to', render: (r) => titleCase(r.applies_to) },
                            { key: 'basis', label: 'Basis', render: (r) => titleCase(r.basis) },
                            { key: 'rate', label: 'Rate %', align: 'right' },
                            { key: 'amount', label: 'Amount $', align: 'right' },
                            { key: 'is_active', label: 'Active', render: (r) => (r.is_active ? <Pill tone="good">on</Pill> : <Pill>off</Pill>) },
                        ]}
                        rows={fees.map((f) => ({ ...f, _key: f.fee_item_id }))}
                        onRowClick={canEdit ? (r) => setFee({ ...r }) : undefined}
                        empty="No fee template is loaded."
                    />
                </>
            )}

            {tab === 'defaults' && settings && (
                <div className="max-w-2xl">
                    <Input label="Default reserve floor for a new owner's book ($)" type="number" min="0" step="0.01"
                        value={settings.reserve_floor_per_unit ?? ''} disabled={!canEdit}
                        onChange={(e) => setSettings({ ...settings, reserve_floor_per_unit: e.target.value })} />
                    <Textarea label="The base management fee INCLUDES (one per line — printed on agreements)" rows={6}
                        value={scopeText.included} disabled={!canEdit} onChange={(e) => setScopeText({ ...scopeText, included: e.target.value })} />
                    <Textarea label="The base management fee does NOT include (one per line)" rows={5}
                        value={scopeText.excluded} disabled={!canEdit} onChange={(e) => setScopeText({ ...scopeText, excluded: e.target.value })} />
                    {canEdit && <Button disabled={busy} onClick={saveDefaults}>Save defaults</Button>}
                </div>
            )}

            {layer && (
                <Modal title={`${layer.name} — ${titleCase(layer.layer)} layer`} subtitle={layer.citation} onClose={() => setLayer(null)} wide>
                    {LAYER_FIELDS.map(([group, fields]) => (
                        <div key={group} className="mb-4">
                            <h4 className="text-sm font-semibold text-slate-700 mb-2">{group}</h4>
                            <div className="grid sm:grid-cols-3 gap-x-4">
                                {fields.map(([k, lbl, kind]) => (Array.isArray(kind)
                                    ? <Select key={k} label={lbl} value={layer[k] || kind[0]} options={kind} onChange={(e) => setLayer({ ...layer, [k]: e.target.value })} />
                                    : kind === 'bool'
                                        ? <label key={k} className="flex items-center gap-2 text-sm mb-3 mt-5"><input type="checkbox" checked={!!Number(layer[k])} onChange={(e) => setLayer({ ...layer, [k]: e.target.checked ? 1 : 0 })} />{lbl}</label>
                                        : <Input key={k} label={lbl} type="number" step="any" value={layer[k] ?? ''} onChange={(e) => setLayer({ ...layer, [k]: e.target.value })} />))}
                            </div>
                        </div>
                    ))}
                    <Textarea label="Notes (source of each figure, e.g. City Comptroller notice of 2 Jan 2027)" value={layer.notes || ''} onChange={(e) => setLayer({ ...layer, notes: e.target.value })} />
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => setLayer(null)}>Cancel</Button>
                        <Button disabled={busy} onClick={saveLayer}>Save</Button>
                    </div>
                </Modal>
            )}

            {fee && (
                <Modal title={`Fee template — ${fee.label}`} onClose={() => setFee(null)}>
                    <Input label="Label" value={fee.label || ''} onChange={(e) => setFee({ ...fee, label: e.target.value })} />
                    <Select label="Basis" value={fee.basis} options={BASES} onChange={(e) => setFee({ ...fee, basis: e.target.value })} />
                    <div className="grid grid-cols-2 gap-x-4">
                        <Input label="Rate (%) — for a percent basis" type="number" step="any" value={fee.rate ?? ''} onChange={(e) => setFee({ ...fee, rate: e.target.value })} />
                        <Input label="Amount ($) — for a flat basis" type="number" step="any" value={fee.amount ?? ''} onChange={(e) => setFee({ ...fee, amount: e.target.value })} />
                        <Input label="Minimum ($)" type="number" step="any" value={fee.minimum_amount ?? ''} onChange={(e) => setFee({ ...fee, minimum_amount: e.target.value })} />
                        <Input label="Maximum ($)" type="number" step="any" value={fee.maximum_amount ?? ''} onChange={(e) => setFee({ ...fee, maximum_amount: e.target.value })} />
                    </div>
                    <Textarea label="Notes" value={fee.notes || ''} onChange={(e) => setFee({ ...fee, notes: e.target.value })} />
                    <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={!!fee.is_active} onChange={(e) => setFee({ ...fee, is_active: e.target.checked })} /> Included in new agreements</label>
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => setFee(null)}>Cancel</Button>
                        <Button disabled={busy} onClick={saveFee}>Save</Button>
                    </div>
                </Modal>
            )}
        </Panel>
    );
}
